/* My Activity — the student's transparency log, adapted to the fullstack's
 * staged-decision model. The prototype keeps an in-memory per-edit feed of the
 * shared team company; the fullstack stages decisions client-side and persists
 * them per resolved quarter, so this screen shows two real, available sources:
 *   1. "This quarter" — your staged (pending) decisions, decoded into readable
 *      lines grouped by the screen you changed them on.
 *   2. "Quarter-by-quarter log" — the company's resolved-quarter news feed (the
 *      engine's narrative of what happened), filterable by quarter.
 * Read-only; no decisions are made here. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { fmtM } from '@venturewrist/engine';
import type { RoundDecisions } from '@venturewrist/shared';
import { useDecisions, pendingCount } from '../../store/decisions';
import type { ScreenProps } from './types';
import '../../styles/activity.css';

type Entry = { screen: string; label: string };

/* Decode the staged decision bundle into readable per-screen activity lines. */
function decodeDraft(d: RoundDecisions, nameOf: (id: string) => string): Entry[] {
  const out: Entry[] = [];
  const add = (screen: string, label: string) => out.push({ screen, label });
  for (const p of d.products ?? []) {
    const nm = nameOf(p.id);
    for (const g of p.gens ?? []) {
      if (g.price != null) add('Pricing', `${nm} Gen ${g.gen} price → $${Math.round(g.price)}`);
      if (g.production != null) add('Production', `${nm} Gen ${g.gen} production → ${g.production}k`);
      if (g.alloc) add('Production', `${nm} Gen ${g.gen} source allocation`);
      if (g.spec) add('Design', `${nm} Gen ${g.gen} spec edit`);
    }
    if (p.name) add('Design', `Renamed line → ${p.name}`);
    if (p.targetSeg) add('Design', `${nm} target segment → ${p.targetSeg}`);
    if (p.retire) add('Design', `Retired ${nm}`);
    if (p.mkt) add('Marketing', `${nm} campaign updated`);
    if (p.newGen) add('Design', `${nm} new generation engineered`);
  }
  for (const np of d.newProducts ?? []) add('Design', `New product designed (${np.seg})`);
  if (d.suppliers) for (const k of Object.keys(d.suppliers)) add('Supply', `${k} supplier changed`);
  if (d.rnd) {
    if (d.rnd.budget != null) add('Research', `R&D budget → ${fmtM(d.rnd.budget)}/qtr`);
    if (d.rnd.active !== undefined) add('Research', d.rnd.active ? 'Active research set' : 'Research stopped');
    if (d.rnd.queue) add('Research', 'Research queue updated');
  }
  for (const e of d.distribution ?? []) add('Distribution', `Activated ${e.method} in ${e.country}`);
  for (const sb of d.storeBuilds ?? []) add('Distribution', `Build ${sb.count} store(s) in ${sb.country}`);
  for (const cc of d.flagship ?? []) add('Distribution', `Flagship store in ${cc}`);
  if (d.allocMode) add('Production', `Allocation mode → ${d.allocMode}`);
  for (const b of d.factoryBuilds ?? []) add('Production', `Build factory in ${b.city} (${b.cap}k)`);
  for (const o of d.factoryOps ?? []) add('Production', `Factory ${o.op}`);
  for (const c of d.contracts ?? []) add('Production', `Supply contract with ${c.partnerId}`);
  for (const id of d.expansions ?? []) add('Production', `Capacity expansion: ${id}`);
  if (d.whAlloc) add('Logistics', 'Warehouse shipment allocation');
  if (d.whAuto) add('Logistics', 'Warehouse auto-allocate');
  for (const op of d.genOps ?? []) add('Production', `Generation ${op.op}`);
  if (d.brandStrat) add('Marketing', `Brand strategy → ${d.brandStrat}`);
  if (d.freightMode) add('Logistics', `Freight mode → ${d.freightMode}`);
  for (const b of d.whBuilds ?? []) add('Logistics', `Build warehouse in ${b.city}`);
  if (d.whExpand) for (const _ of Object.keys(d.whExpand)) add('Logistics', 'Warehouse expansion');
  for (const _ of d.whClose ?? []) add('Logistics', 'Warehouse closure');
  const f = d.financing;
  if (f) {
    for (const l of f.loans ?? []) add('Financing', `Loan drawn ${fmtM(l.amount)} (${l.tenor}q)`);
    for (const b of f.bonds ?? []) add('Financing', `Bond issued ${fmtM(b.face)} (${b.tenor}q)`);
    if (f.equityRaise) add('Financing', `Equity raise ${fmtM(f.equityRaise)}`);
    if (f.buyback) add('Financing', `Share buyback ${fmtM(f.buyback)}`);
    if (f.divRatio != null) add('Financing', `Dividend policy → ${Math.round(f.divRatio * 100)}%`);
  }
  return out;
}

export function MyActivity({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const [rd, setRd] = useState<string>('all');

  const s = detail.state as any;
  const nameOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of (s.products ?? []) as any[]) m[p.id] = p.name;
    return (id: string) => m[id] ?? id;
  }, [s.products]);

  const entries = useMemo(() => decodeDraft(draft, nameOf), [draft, nameOf]);
  const screensTouched = new Set(entries.map((e) => e.screen)).size;
  const pending = pendingCount(draft);
  const quartersPlayed = (s.history?.length ?? 0) as number;
  const news = (s.news ?? []) as any[];
  const whens = [...new Set(news.map((n) => n.when))];
  const shownNews = rd === 'all' ? news : news.filter((n) => n.when === rd);

  return (
    <div className="ac-root">
      <div className="screen-head">
        <h2>📝 My Activity</h2>
        <div className="sub">Your decision log for {detail.game.companyName} — what you've staged this quarter, plus the company's quarter-by-quarter record. A transparency log and a study aid for what you changed and why.</div>
      </div>

      <div className="grid4" style={{ marginBottom: 14 }}>
        <div className="card"><div className="sub">Pending decisions (this qtr)</div><div className="big-num">{pending}</div></div>
        <div className="card"><div className="sub">Screens touched</div><div className="big-num">{screensTouched}</div></div>
        <div className="card"><div className="sub">Quarters played</div><div className="big-num">{quartersPlayed}</div></div>
        <div className="card"><div className="sub">Quarter log entries</div><div className="big-num">{news.length}</div></div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h3>This quarter — staged decisions</h3>
        {entries.length === 0 ? (
          <div className="sub">No staged changes yet. Edits you make on the decision screens appear here until you submit the quarter.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {entries.map((e, i) => (
              <div key={i} className="act-row">
                <span style={{ opacity: 0.7, width: 16, textAlign: 'center' }}>✏️</span>
                <span className="act-scr">{e.screen}</span>
                <span style={{ flex: 1 }}>{e.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="spread" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Quarter-by-quarter log</h3>
          {whens.length > 1 ? (
            <div className="opt-row">
              <button className={'opt-pill' + (rd === 'all' ? ' on' : '')} onClick={() => setRd('all')}>All</button>
              {whens.map((w) => <button key={w} className={'opt-pill' + (rd === w ? ' on' : '')} onClick={() => setRd(w)}>{w}</button>)}
            </div>
          ) : null}
        </div>
        {shownNews.length === 0 ? (
          <div className="empty-state"><div className="ic">📭</div>No quarter-log entries yet — they appear as you resolve quarters.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {shownNews.map((n, i) => (
              <div key={i} className="act-row">
                <span style={{ opacity: 0.8, width: 18, textAlign: 'center' }}>{n.icon || '•'}</span>
                {n.when ? <span className="chip" style={{ minWidth: 64 }}>{n.when}</span> : null}
                <span style={{ flex: 1 }}>{n.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
